import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormikProps } from 'formik';

import type { RolesDTO } from 'store/roles/rolesSlice';

import { notifyUserError, notifyUserSuccess } from '../../utils/notifications';
import {
  deleteUser,
  getAboutOrganization,
  getAiScribe,
  getBillingConfig,
  getBillingPrograms,
  getСadence,
  getCadenceUnits,
  getMedicalConfig,
  getCHWReport,
  getCoordinatorsReport,
  getIntegrationsEMR,
  getIntegrationsEMRMessageTypes,
  getIsProgramActive,
  getOrganizationConnections,
  getOrganizationsList,
  getOrganizationsListAutocomplete,
  getProgram,
  getProgramList,
  getProgramListAutocomplete,
  getProgramsReport,
  getProviders,
  getSitesConfig,
  getSitesList,
  getUser,
  getUserListAutocomplete,
  getUsersImage,
  getUsersList,
  getValidPrograms,
  patchChangeOrganizationStatus,
  patchChangeProgramStatus,
  postAboutPageCreate,
  postActivateOrganization,
  postActivateProgram,
  postResendInvite,
  postSite,
  postUserCreate,
  putAboutPageCreate,
  putAiScribe,
  putMedicalConfig,
  putCreateProgream,
  putIntegrationsEMR,
  putOrganizationSSO,
  putUpdateDoingTheWork,
  putUpdateGettingBetter,
  putUpdateUser,
  putСadence,
  putBillingConfig,
  putSite,
  putSitesConfig,
  getStatuses
} from './organizationActions';

export interface AboutPageProps {
  name?: string | undefined;
  logoPath?: string | undefined;
  logoUrl?: string | undefined;
  description?: string | undefined;
  contractConfirmation?: boolean | undefined;
  isActive?: boolean | undefined;
  sso: SSOProps;
}

export interface ProgramContentItem {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  valid: boolean;
  createdBy: string;
  updatedBy: string;
}
export interface ReportingListItem {
  id: number;
  name: string;
  profileImageKey?: string;
}

export interface CHWSItem {
  id: number;
  name: string;
  profileImageKey: string;
}

export interface ProgramValidItem {
  id: number;
  name: string;
  buildingProgramValid: boolean;
  doingTheWorkValid: boolean;
}

export interface Site {
  id: number;
  newSite?: boolean;
  siteName: string;
  status: 'ACTIVE' | 'INACTIVE';
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode: string;
}

export type Preset = {
  id: number;
  title: string;
  isCore: boolean;
  enabled: boolean;
  todos: {
    id?: number;
    title: string;
    newlyAdded?: boolean;
  }[];
};

type Interval = 'WEEK' | 'MONTH';

export type GettingBetterType = {
  engagementAttempt: {
    interval: number;
    intervalType: Interval;
  };
  engagement: {
    interval: number;
    intervalType: Interval;
  };
  inPersonEngagement: {
    interval: number;
    intervalType: Interval;
  };
  supervisorReview: {
    interval: number;
    intervalType: Interval;
  };
};

export interface ProgramDetails {
  status?: string;
  organizationStatus: string;
  id?: number;
  name?: string;
  sites?: Site[];
  programHiring?: {
    campaigns: number[];
    chwExisting: number;
    chwNew: number;
    managerExisting: number;
    managerNew: number;
    directorExisting: number;
    directorNew: number;
    coordinatorExisting: number;
    coordinatorNew: number;
  };
  eligibilityCriteria: {
    geographyEnabled: boolean;
    zipCodes: { name: string; id: number }[];
    languageEnabled: boolean;
    languages: { name: string; id: number }[];
    insuranceEnabled: boolean;
    insurances: { name: string; id: number }[];
    healthConditionEnabled: boolean;
    healthConditions: { name: string; id: number; description: string }[];
    priorHospitalsVisitsEnabled: boolean;
    priorHospitalsVisitsCondition: string;
    priorHospitalsVisitsValue: number;
    socialNeedsEnabled: boolean;
    referralEnabled: boolean;
    pregnancyIncludeEnabled: boolean;
    rulesForEligibilityEnabled: boolean;
    rulesForEligibility: string;
  };
  enrollmentGoals: {
    chwNumber: number;
    recommendedAverageAnnualEnrollment: number;
  };
  maximumCaseload: {
    maximumChwCaseload: number;
  };
  autoConnectSettings: {
    autoConnectFrequencyMinutes: string;
  };
  programModalityAndDuration: {
    type: string;
    zipCodes: { name: string; id: number }[];
    duration: number;
    period: string;
    indefinite: boolean;
  };
  presets: Preset[];
  gettingBetter: GettingBetterType;
}

export interface ProgramPageProps {
  name: string;
  programsList: {
    content: ProgramContentItem[] | [];
    page: number;
    size: number;
    totalNumber: number;
  };
  programsListAutocomplete: AutocompleteItem[];
  programsListLoading: boolean;
  programDetails: {
    loading: boolean;
    details: ProgramDetails | null;
  };
  programsListValid: {
    programs: ProgramValidItem[];
    hasValidProgram: boolean;
  };
}

export interface OrganizationsContentItem {
  id: number;
  name: string;
  status: string;
  eligibleStatuses: string[];
  logoUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface UsersContentItem {
  id: number;
  name: string;
  status: string;
  email: string;
  programs: string[];
  roles: string[];
  activationDateTime: string;
  isInitialUser: boolean;
  locked: boolean;
  profileImageKey: string;
  profileImageUrl?: string;
}

export interface AutocompleteItem {
  id: number;
  name: string;
}

export interface OrganizationsListProps {
  organizationsList: {
    content: OrganizationsContentItem[] | [];
    page: number;
    size: number;
    totalNumber: number;
  };
  organizationsListAutocomplete: AutocompleteItem[];
  organizationsListLoading: boolean;
}

export interface UsersListProps {
  usersList: {
    content: UsersContentItem[];
    page: number;
    size: number;
    totalNumber: number;
    customerRoles: number;
    impactRoles: number;
    pendingUsers: number;
  };
  usersListLoading: boolean;
  usersListAutocomplete: AutocompleteItem[];
}

export interface IntegrationsPropsEMRItem {
  id?: number;
  note: string | null;
  directAddress: string | null;
}

export interface IntegrationsEMRMessageTypesItem {
  id: string;
  name: string;
}

export interface IntegrationsPropsEMR {
  enabled: boolean;
  messageType: string | null;
  faxNumber?: string | null;
  // Remove this after enableEfaxIntegration flag is removed
  directAddresses: IntegrationsPropsEMRItem[];
  integrationsEMRMessageTypes?: IntegrationsEMRMessageTypesItem[];
}

export type CadenceUnitsType = 'DAYS' | 'WEEKS';

export interface CadenceUnitsItem {
  id: CadenceUnitsType;
  name: string;
}

export interface CadenceProps {
  enabled: boolean;
  cadence: number | null;
  unit: CadenceUnitsType | null;
  sftpId?: string;
}

type InviteStatus = 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'EXPIRED';

export interface Invite {
  dateTime: string;
  status: InviteStatus;
}

type LinkedUser = {
  name: string;
  surname: string;
  organizationId: number;
  organizationName: string;
};
export interface User {
  id: number;
  name: string;
  surname: string;
  userId: string;
  email: string;
  phoneNumber: string;
  status: string;
  eligibleStatuses: string[];
  organizationId: 0;
  programs: Pick<ProgramContentItem, 'id' | 'name'>[];
  roles: RolesDTO[];
  invites: Invite[];
  isInitialUser: boolean;
  linkedUsers: LinkedUser[];
}

export interface AiScribeProps {
  loading: boolean;
  data: {
    isEnabled: boolean;
  } | null;
}

export interface MedicalConfigProps {
  organizationId?: number;
  enabled: boolean;
  editingAllowed: boolean;
  dataFeedEnabled?: boolean;
  dataFeedConfig?: {
    csvHeaderForIdentifierName?: string;
    csvHeaderForFirstName?: string;
    csvHeaderForLastName?: string;
    csvHeaderForDOBName?: string;
    csvHeaderForMRNOrIdName?: string;
    csvHeaderForDateOfEncounterName?: string;
    csvHeaderForTimeOfEncounterName?: string;
    csvHeaderForTypeName?: string;
    csvHeaderForLocationName?: string;
    csvHeaderForProviderName?: string;
    csvHeaderForReasonName?: string;
  };
  hospitalizationVisitConfig?: {
    hospitalizationVisitTypes?: { name: string }[];
  };
}

export interface BillingConfigProps {
  data: {
    enableBilling: boolean;
    providers: number[];
    billingPrograms: number[];
  };
  loading: boolean;
}

export interface ProviderItem {
  id: number;
  name: string;
}

export interface BillingProgramItem {
  id: number;
  name: string;
}

export interface SiteItem {
  id: number;
  newSite?: boolean;
  siteName: string;
  status: 'ACTIVE' | 'INACTIVE';
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode: string;
  programIds: string[];
  programNames: string[];
  programs: { id: number; name: string }[];
}

export interface SitesConfigProps {
  sitesList: {
    content: SiteItem[];
    size: number;
    page: number;
    totalNumber: number;
    sitesListLoading: boolean;
  };
  enableWhatSpecificSiteFieldForNoteDetails: boolean;
  hasActiveSites: boolean;
}

export interface StatusesItem {
  id: string;
  name: string;
  category: string;
}

export interface OrganizationSliceProps {
  organizations: OrganizationsListProps;
  about: AboutPageProps | null;
  program: ProgramPageProps;
  programsListReport: ProgramContentItem[];
  chwsListReport: CHWSItem[];
  coordinatorsListReport: CHWSItem[];
  confirmableRouteChange: LinkChangeProps;
  users: UsersListProps;
  integrationsEMR: IntegrationsPropsEMR;
  aiScribe: AiScribeProps;
  billing: BillingConfigProps;
  cadenceImportSFTP: CadenceProps;
  cadenceUnits: CadenceUnitsItem[];
  selectedUser: { details: User | null; loading: boolean };
  auth0ConnectionList: string[];
  medical: MedicalConfigProps | null;
  providers: ProviderItem[];
  billingPrograms: BillingProgramItem[];
  sites: SitesConfigProps;
  statuses: StatusesItem[];
}
export interface LinkChangeProps {
  formik: {
    isValid: FormikProps<any>['isValid'];
    submitForm: FormikProps<any>['submitForm'];
  } | null;
  hasUnsavedWork: boolean;
  openConfirmationModal: boolean;
  linkToGo: null | string;
}

export interface SSOProps {
  federated: boolean;
  auth0Connection: string | null;
}

// LINK CHANGE CONFIRM MODAL
const confirmableRouteChange: LinkChangeProps = {
  hasUnsavedWork: false,
  openConfirmationModal: false,
  linkToGo: null,
  formik: null
};
const SSOOrganization: SSOProps = {
  federated: false,
  auth0Connection: null
};
const aboutOrganization: AboutPageProps = {
  name: '',
  logoPath: '',
  description: '',
  contractConfirmation: false,
  isActive: false,
  sso: SSOOrganization
};
const programOrganization: ProgramPageProps = {
  name: 'General Adult Program',
  programsList: {
    content: [],
    page: 0,
    size: 10,
    totalNumber: 0
  },
  programsListAutocomplete: [],
  programsListLoading: true,
  programsListValid: {
    programs: [],
    hasValidProgram: false
  },
  programDetails: {
    loading: false,
    details: null
  }
};
const organizationsList: OrganizationsListProps = {
  organizationsList: {
    content: [],
    page: 0,
    size: 5,
    totalNumber: 0
  },
  organizationsListAutocomplete: [],
  organizationsListLoading: true
};

const usersList: UsersListProps = {
  usersList: {
    content: [],
    page: 0,
    size: 10,
    totalNumber: 0,
    customerRoles: 0,
    impactRoles: 0,
    pendingUsers: 0
  },
  usersListLoading: true,
  usersListAutocomplete: []
};

const integrationOrganizationEMR: IntegrationsPropsEMR = {
  enabled: false,
  messageType: '',
  directAddresses: [],
  integrationsEMRMessageTypes: []
};

const cadenceOrganizationImportSFTP: CadenceProps = {
  enabled: false,
  cadence: null,
  unit: 'DAYS'
};

const medicalConfig: MedicalConfigProps = {
  enabled: false,
  editingAllowed: false,
  dataFeedEnabled: false,
  dataFeedConfig: {},
  hospitalizationVisitConfig: { hospitalizationVisitTypes: [] }
};

const initialState: OrganizationSliceProps = {
  organizations: organizationsList,
  about: aboutOrganization,
  program: programOrganization,
  programsListReport: [],
  chwsListReport: [],
  coordinatorsListReport: [],
  confirmableRouteChange,
  users: usersList,
  integrationsEMR: integrationOrganizationEMR,
  aiScribe: {
    loading: false,
    data: null
  },
  cadenceImportSFTP: cadenceOrganizationImportSFTP,
  cadenceUnits: [],
  selectedUser: {
    details: null,
    loading: false
  },
  auth0ConnectionList: [],
  medical: medicalConfig,
  billing: {
    data: {
      enableBilling: false,
      providers: [],
      billingPrograms: []
    },
    loading: false
  },
  providers: [],
  billingPrograms: [],
  sites: {
    sitesList: { content: [], size: 10, page: 0, totalNumber: 0, sitesListLoading: false },
    enableWhatSpecificSiteFieldForNoteDetails: false,
    hasActiveSites: false
  },
  statuses: []
};

export const organizationSlice = createSlice({
  name: 'organizationSlice',
  initialState,
  reducers: {
    resetAboutOrganization: (state) => {
      state.about = aboutOrganization;
    },
    resetAutocompleteList: (state) => {
      state.organizations.organizationsListAutocomplete = [];
    },
    resetAutocompleteProgramsList: (state) => {
      state.program.programsListAutocomplete = [];
    },
    resetAutocompleteUsersList: (state) => {
      state.users.usersListAutocomplete = [];
    },
    resetSites: (state) => {
      state.sites = initialState.sites;
    },
    // LINK CHANGE CONFIRM MODAL
    setHasUnsavedWork: (
      state,
      action: PayloadAction<{
        formik: {
          isValid: FormikProps<any>['isValid'];
          submitForm: FormikProps<any>['submitForm'];
        } | null;
        hasUnsavedWork: boolean;
      }>
    ) => {
      state.confirmableRouteChange.formik = action.payload.formik;
      state.confirmableRouteChange.hasUnsavedWork = action.payload.hasUnsavedWork;
    },
    setLinkToGo: (state, action) => {
      state.confirmableRouteChange.linkToGo = action.payload;
    },
    setConfirmationModal: (state, action) => {
      state.confirmableRouteChange.openConfirmationModal = action.payload;
    }
    // END LINK CHANGE CONFIRM MODAL
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAboutOrganization.fulfilled, (state: any, { payload }: any) => {
        state.about = payload;
      })
      .addCase(getAboutOrganization.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(postAboutPageCreate.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess(`The organization ${payload.name} was successfully added to the system.`);
      })
      .addCase(postAboutPageCreate.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getOrganizationsList.pending, (state: any) => {
        state.organizations.organizationsListLoading = true;
      })
      .addCase(getOrganizationsList.fulfilled, (state: any, { payload }: any) => {
        state.organizations.organizationsList = payload;
        state.organizations.organizationsListLoading = false;
      })
      .addCase(getOrganizationsList.rejected, (state: any, { payload }: any) => {
        state.organizations.organizationsListLoading = false;
        notifyUserError(payload);
      })
      .addCase(getOrganizationsListAutocomplete.fulfilled, (state: any, { payload }: any) => {
        state.organizations.organizationsListAutocomplete = payload;
      })
      .addCase(getProgramListAutocomplete.fulfilled, (state: any, { payload }: any) => {
        state.program.programsListAutocomplete = payload;
      })
      .addCase(getUsersList.pending, (state: any) => {
        state.users.usersListLoading = true;
      })
      .addCase(getUsersList.fulfilled, (state: any, { payload }: any) => {
        state.users.usersList = payload;
        state.users.usersListLoading = false;
      })
      .addCase(getUsersList.rejected, (state: any, { payload }: any) => {
        state.users.usersListLoading = false;
        notifyUserError(payload);
      })
      .addCase(getUserListAutocomplete.fulfilled, (state: any, { payload }: any) => {
        state.users.usersListAutocomplete = payload;
      })
      .addCase(getProgramList.pending, (state: any) => {
        state.users.programsListLoading = true;
      })
      .addCase(getProgramList.fulfilled, (state: any, { payload }: any) => {
        state.program.programsList = payload;
        state.users.programsListLoading = false;
      })
      .addCase(getProgramList.rejected, (state: any, { payload }: any) => {
        state.users.programsListLoading = false;
        notifyUserError(payload);
      })
      .addCase(putAboutPageCreate.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess("Your changes were saved. You're all set.");
      })
      .addCase(putAboutPageCreate.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(postUserCreate.fulfilled, (state, { payload, meta }) => {
        state.users.usersList.content.unshift(payload);
        notifyUserSuccess(`${payload.name} added to the organization`);
      })
      .addCase(postUserCreate.rejected, (state, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getUser.pending, (state) => {
        state.selectedUser.loading = true;
      })
      .addCase(getUser.fulfilled, (state, { payload }) => {
        state.selectedUser = {
          details: payload,
          loading: false
        };
      })
      .addCase(putUpdateUser.fulfilled, (state, { payload }) => {
        const userIndex = state.users.usersList.content.findIndex((user) => user.id === payload.id);
        state.users.usersList.content[userIndex] = payload;
        notifyUserSuccess('User updated successfully');
      })
      .addCase(postResendInvite.fulfilled, (state, { payload }) => {
        notifyUserSuccess('Invite resent successfully');
      })
      .addCase(deleteUser.rejected, (state, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getProgram.pending, (state) => {
        state.program.programDetails.loading = true;
      })
      .addCase(getProgram.fulfilled, (state, { payload }) => {
        state.program.programDetails = {
          details: { ...state.program.programDetails.details, ...payload },
          loading: false
        };
      })
      .addCase(getIsProgramActive.pending, (state) => {})
      .addCase(getIsProgramActive.fulfilled, (state, { payload }) => {})
      .addCase(putCreateProgream.fulfilled, (state, { payload, meta }) => {
        if (state.program.programDetails.details) {
          state.program.programDetails = {
            ...state.program.programDetails,
            details: { ...state.program.programDetails.details, name: meta.arg.program.name }
          };
        }
      })
      .addCase(postActivateProgram.fulfilled, (state, { payload }) => {
        notifyUserSuccess('Program activated successfully');
      })
      .addCase(postActivateProgram.rejected, (state, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putUpdateDoingTheWork.fulfilled, (state) => {
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putUpdateDoingTheWork.rejected, (state, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putUpdateGettingBetter.fulfilled, (state) => {
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putUpdateGettingBetter.rejected, (state, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getIntegrationsEMR.fulfilled, (state: any, { payload }: any) => {
        state.integrationsEMR = {
          ...payload,
          integrationsEMRMessageTypes: state.integrationsEMR.integrationsEMRMessageTypes
        };
      })
      .addCase(getIntegrationsEMR.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getIntegrationsEMRMessageTypes.fulfilled, (state: any, { payload }: any) => {
        state.integrationsEMR = { ...state.integrationsEMR, integrationsEMRMessageTypes: payload };
      })
      .addCase(getIntegrationsEMRMessageTypes.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putIntegrationsEMR.fulfilled, (state: any, { payload }: any) => {
        state.integrationsEMR = {
          ...payload,
          integrationsEMRMessageTypes: state.integrationsEMR.integrationsEMRMessageTypes
        };
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putIntegrationsEMR.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getСadence.fulfilled, (state: any, { payload }: any) => {
        state.cadenceImportSFTP = payload;
      })
      .addCase(getСadence.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getCadenceUnits.fulfilled, (state: any, { payload }: any) => {
        state.cadenceUnits = payload;
      })
      .addCase(getCadenceUnits.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putСadence.fulfilled, (state: any, { payload }: any) => {
        state.cadenceImportSFTP = payload;
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putСadence.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getValidPrograms.fulfilled, (state: any, { payload }: any) => {
        state.program.programsListValid = payload;
      })
      .addCase(getValidPrograms.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(postActivateOrganization.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess(`${state.about.name} was successfully activated`);
        state.program.programsListValid = { ...state.program.programsListValid, payload };
      })
      .addCase(patchChangeOrganizationStatus.fulfilled, (state, { meta }) => {
        const index = state.organizations.organizationsList.content.findIndex(
          (org) => org.id === meta.arg.data.organizationID
        );

        if (meta.arg.data.status === 'INACTIVE') {
          state.organizations.organizationsList.content[index].status = 'INACTIVATION_PENDING';

          notifyUserSuccess(
            'All IMPaCT Leaders have received an email notification of this inactivation request. If no action is taken, in 3 days, all users will be notified of inactivation after a 30-day grace period.'
          );
          return;
        }

        state.organizations.organizationsList.content[index].status = meta.arg.data.status;
      })
      .addCase(patchChangeOrganizationStatus.rejected, () => {
        notifyUserError('Something went wrong. Please try again later.');
      })
      .addCase(patchChangeProgramStatus.fulfilled, (state, { meta }) => {
        const index = state.program.programsList.content.findIndex(
          (program) => program.id === meta.arg.data.programID
        );

        if (meta.arg.data.status === 'INACTIVE') {
          state.program.programsList.content[index].status = 'INACTIVATION_PENDING';

          return;
        }

        state.program.programsList.content[index].status = meta.arg.data.status;
      })
      .addCase(patchChangeProgramStatus.rejected, () => {
        notifyUserError('Something went wrong. Please try again later.');
      })
      .addCase(postActivateOrganization.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getUsersImage.fulfilled, (state: any, { payload }: any) => {
        const newArr = state.users.usersList.content;
        const found = newArr.findIndex((a: { id: number }) => a.id === payload.userId);
        newArr[found].profileImageUrl = URL.createObjectURL(new Blob([payload.data]));
        state.users.usersList = { ...state.users.usersList, content: newArr };
      })
      .addCase(getUsersImage.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getOrganizationConnections.fulfilled, (state: any, { payload }: any) => {
        let selectData = payload;
        if (state.about?.sso?.auth0Connection) {
          selectData = [...selectData, state.about?.sso?.auth0Connection];
        }

        state.auth0ConnectionList = selectData;
      })
      .addCase(getOrganizationConnections.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putOrganizationSSO.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putOrganizationSSO.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getProgramsReport.fulfilled, (state: any, { payload }: any) => {
        state.programsListReport = payload;
      })
      .addCase(getProgramsReport.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getCHWReport.fulfilled, (state: any, { payload }: any) => {
        state.chwsListReport = payload;
      })
      .addCase(getCHWReport.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getCoordinatorsReport.fulfilled, (state: any, { payload }: any) => {
        state.coordinatorsListReport = payload;
      })
      .addCase(getCoordinatorsReport.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getAiScribe.pending, (state: any) => {
        state.aiScribe.loading = true;
      })
      .addCase(getAiScribe.fulfilled, (state, { payload }: any) => {
        state.aiScribe = {
          data: {
            isEnabled: payload.isEnabled
          },
          loading: false
        };
      })
      .addCase(getAiScribe.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
        state.aiScribe.loading = false;
      })
      .addCase(putAiScribe.fulfilled, (state: any, { payload }: any) => {
        state.aiScribe = {
          isEnabled: payload.isEnabled
        };
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putAiScribe.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getMedicalConfig.fulfilled, (state, { payload }: any) => {
        state.medical = payload;
      })
      .addCase(getMedicalConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putMedicalConfig.fulfilled, (state: any, { payload }: any) => {
        state.medical = payload;
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putMedicalConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getBillingConfig.fulfilled, (state, { payload }: any) => {
        state.billing.data = payload;
      })
      .addCase(getBillingConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putBillingConfig.fulfilled, (state, { meta }) => {
        notifyUserSuccess('Your changes were saved.');
        state.billing.data = {
          billingPrograms: meta.arg.data.billingPrograms,
          providers: meta.arg.data.providers,
          enableBilling: meta.arg.data.enableBilling
        };
      })
      .addCase(putBillingConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getProviders.fulfilled, (state: any, { payload }: any) => {
        state.providers = payload;
      })
      .addCase(getProviders.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getBillingPrograms.fulfilled, (state: any, { payload }: any) => {
        state.billingPrograms = payload;
      })
      .addCase(getBillingPrograms.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getSitesConfig.fulfilled, (state, { payload }: any) => {
        state.sites.enableWhatSpecificSiteFieldForNoteDetails =
          payload.enableWhatSpecificSiteFieldForNoteDetails;
        state.sites.hasActiveSites = payload.hasActiveSites;
      })
      .addCase(getSitesConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putSitesConfig.fulfilled, (state: any, { payload }: any) => {
        state.sites.enableWhatSpecificSiteFieldForNoteDetails =
          payload.enableWhatSpecificSiteFieldForNoteDetails;
        state.sites.hasActiveSites = payload.hasActiveSites;
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putSitesConfig.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getSitesList.pending, (state: any) => {
        state.sites.sitesList.sitesListLoading = true;
      })
      .addCase(getSitesList.fulfilled, (state: any, { payload }: any) => {
        state.sites.sitesList.content = payload.content.map((site: any) => ({
          ...site,
          programNames: site.programs.map((program: any) => program.name).join(', '),
          programIds: site.programs.map((program: any) => program.id.toString())
        }));
        state.sites.sitesList.size = payload.size;
        state.sites.sitesList.page = payload.page;
        state.sites.sitesList.totalNumber = payload.totalNumber;
        state.sites.sitesList.sitesListLoading = false;
      })
      .addCase(getSitesList.rejected, (state: any, { payload }: any) => {
        state.sites.sitesList.sitesListLoading = false;
        notifyUserError(payload);
      })
      .addCase(postSite.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(postSite.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putSite.fulfilled, (state: any, { payload }: any) => {
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putSite.rejected, (state: any, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getStatuses.fulfilled, (state: any, { payload }: any) => {
        state.statuses = payload;
      });
  }
});

export const {
  resetAboutOrganization,
  resetAutocompleteList,
  setHasUnsavedWork,
  setLinkToGo,
  setConfirmationModal,
  resetAutocompleteProgramsList,
  resetAutocompleteUsersList,
  resetSites
} = organizationSlice.actions;
export default organizationSlice.reducer;
